import React, { useEffect, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { getPrograms } from "../../lib/api";
import { createProgram, deleteProgram, updateProgram } from "../../lib/adminApi";
import { getAdminErrorMessage } from "../../lib/adminForm";
import { AdminField, AdminModal, AdminTable } from "../../components/admin/AdminUi";
import { MediaInput } from "../../components/admin/MediaInput";

const EMPTY = {
  name: "",
  slug: "",
  coverImage: "",
  description: "",
  longDescription: "",
  day: "",
  time: "",
  category: "",
  order: 0,
};

export default function AdminPrograms() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = () => getPrograms().then(setItems).catch(() => setItems([]));

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setError("");
    setOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({ ...item });
    setError("");
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = {
        ...form,
        order: Number(form.order) || 0,
      };
      if (editing) await updateProgram(editing.id, payload);
      else await createProgram(payload);
      setOpen(false);
      try {
        await load();
      } catch {
        // Kayıt başarılı; liste yenileme hatası kayıt hatası gibi gösterilmez.
      }
    } catch (err) {
      setError(getAdminErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Bu program silinecek. İlgili videolar da silinir.")) return;
    await deleteProgram(id);
    await load();
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-4 mb-5">
        <h2 className="font-display font-bold text-2xl text-white">Programlar</h2>
        <button type="button" onClick={openCreate} className="tv-btn-primary !py-2.5 text-sm inline-flex items-center gap-2">
          <Plus size={16} /> Yeni Program
        </button>
      </div>

      <AdminTable columns={["Program", "Gün / Saat", "Kategori", "Sıra", ""]}>
        {items.map((item) => (
          <tr key={item.id} className="border-b border-[var(--tv-border)] last:border-0">
            <td className="px-4 py-3">
              <p className="font-semibold text-white">{item.name}</p>
              <p className="text-xs text-[var(--tv-muted)] mt-0.5">{item.slug}</p>
            </td>
            <td className="px-4 py-3 text-[var(--tv-muted)]">{item.day} · {item.time}</td>
            <td className="px-4 py-3 text-[var(--tv-muted)]">{item.category}</td>
            <td className="px-4 py-3 text-[var(--tv-muted)]">{item.order}</td>
            <td className="px-4 py-3">
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => openEdit(item)} className="tv-btn-ghost !py-1.5 !px-2.5 text-xs">
                  <Pencil size={14} />
                </button>
                <button type="button" onClick={() => remove(item.id)} className="tv-btn-ghost !py-1.5 !px-2.5 text-xs text-red-300">
                  <Trash2 size={14} />
                </button>
              </div>
            </td>
          </tr>
        ))}
      </AdminTable>

      <AdminModal open={open} title={editing ? "Program Düzenle" : "Yeni Program"} onClose={() => setOpen(false)}>
        <form onSubmit={save} className="grid md:grid-cols-2 gap-4">
          <AdminField label="Program adı">
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="tv-input" />
          </AdminField>
          <AdminField label="Slug (URL)">
            <input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className="tv-input" placeholder="otomatik" />
          </AdminField>
          <MediaInput
            label="Kapak görseli"
            className="md:col-span-2"
            value={form.coverImage}
            onChange={(coverImage) => setForm({ ...form, coverImage })}
          />
          <AdminField label="Kısa açıklama" className="md:col-span-2">
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="tv-input resize-none" />
          </AdminField>
          <AdminField label="Detay açıklama" className="md:col-span-2">
            <textarea value={form.longDescription} onChange={(e) => setForm({ ...form, longDescription: e.target.value })} rows={3} className="tv-input resize-none" />
          </AdminField>
          <AdminField label="Gün">
            <input value={form.day} onChange={(e) => setForm({ ...form, day: e.target.value })} className="tv-input" />
          </AdminField>
          <AdminField label="Saat">
            <input value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="tv-input" />
          </AdminField>
          <AdminField label="Kategori">
            <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="tv-input" />
          </AdminField>
          <AdminField label="Sıra">
            <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className="tv-input" />
          </AdminField>
          {error && <p className="md:col-span-2 text-sm text-red-400">{error}</p>}
          <div className="md:col-span-2 flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setOpen(false)} className="tv-btn-ghost">İptal</button>
            <button type="submit" disabled={saving} className="tv-btn-primary">{saving ? "Kaydediliyor..." : "Kaydet"}</button>
          </div>
        </form>
      </AdminModal>
    </div>
  );
}
